import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Download, Edit, Trash2, Eye, Package, PackageCheck, XCircle } from "lucide-react";
import { purchasesAPI, getApiErrorPayload } from "@/lib/api";
import { useCompanyProfile } from "@/hooks/useCompanyProfile";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { formatNumber } from "@/lib/utils";
import { Loading } from "@/components/ui/loader";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { confirmAction } from "@/components/ui/confirm-dialog";

interface PurchaseItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage?: number;
	discountAmount: number;
	taxPercentage?: number;
	taxRate?: number;
	taxAmount: number;
	totalAmount: number;
}

/** Matches the backend PurchaseStatus enum. */
type PurchaseStatus = "pending" | "confirmed" | "received" | "cancelled" | "refunded";

interface PurchaseTransaction {
	id: string;
	invoiceNumber: string;
	supplierId: string;
	supplier?: {
		id: string;
		name: string;
		email: string;
		phone: string;
		address: string;
	};
	items: PurchaseItem[];
	subtotal: number;
	discountAmount: number;
	taxAmount: number;
	shippingAmount: number;
	totalAmount: number;
	paidAmount: number;
	remainingAmount: number;
	paymentStatus: "pending" | "partial" | "paid";
	status: PurchaseStatus;
	notes: string;
	purchaseDate: string;
	expectedDeliveryDate: string;
	createdBy: string;
	createdAt: string;
	updatedAt: string;
}

interface PurchaseUpdatePayload {
	supplierId?: number;
	paymentStatus?: "pending" | "partial" | "paid";
	shippingAmount?: number;
	notes?: string;
	expectedDeliveryDate?: string;
	subtotal?: number;
	taxAmount?: number;
	discountAmount?: number;
	totalAmount?: number;
	paidAmount?: number;
	remainingAmount?: number;
	purchaseDate?: string;
	items: Array<{
		productId: number;
		quantity: number;
		unitPrice: number;
		discountRate?: number;
		discountAmount?: number;
		taxRate?: number;
		taxAmount?: number;
		totalAmount?: number;
	}>;
}

const PurchaseDetailPage: React.FC = () => {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { toast } = useToast();
	const currency = useCurrency();
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [purchaseTransaction, setPurchaseTransaction] = useState<PurchaseTransaction | null>(null);
	const { data: companyProfile } = useCompanyProfile({ enabled: !!id });
	const [pdfLoading, setPdfLoading] = useState(false);
	const [statusUpdating, setStatusUpdating] = useState(false);

	useEffect(() => {
		if (id) {
			fetchPurchaseTransaction();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- fetch on id change only
	}, [id]);

	const fetchPurchaseTransaction = async () => {
		try {
			setLoading(true);
			const data = await purchasesAPI.getById(id!);
			setPurchaseTransaction(data);
		} catch (error) {
			toast({
				title: t("common.error"),
				description: t("purchases.errors.fetchFailed"),
				variant: "destructive",
			});
			navigate("/purchases");
		} finally {
			setLoading(false);
		}
	};

	const handleInputChange = (field: keyof PurchaseTransaction, value: any) => {
		if (purchaseTransaction) {
			setPurchaseTransaction(prev => ({ ...prev!, [field]: value }));
		}
	};

	const buildPurchaseUpdatePayload = (value: PurchaseTransaction): PurchaseUpdatePayload => {
		return {
			// Status is changed only through the status actions, which add stock on receipt.
			supplierId: value.supplierId ? Number(value.supplierId) : undefined,
			paymentStatus: value.paymentStatus,
			shippingAmount: Number(value.shippingAmount) || 0,
			notes: value.notes || undefined,
			expectedDeliveryDate: value.expectedDeliveryDate || undefined,
			subtotal: Number(value.subtotal) || 0,
			taxAmount: Number(value.taxAmount) || 0,
			discountAmount: Number(value.discountAmount) || 0,
			totalAmount: Number(value.totalAmount) || 0,
			paidAmount: Number(value.paidAmount) || 0,
			remainingAmount: Number(value.remainingAmount) || 0,
			purchaseDate: value.purchaseDate || undefined,
			items: (value.items || []).map(item => ({
				productId: Number(item.productId),
				quantity: Number(item.quantity) || 0,
				unitPrice: Number(item.unitPrice) || 0,
				discountRate: Number(item.discountPercentage ?? 0) || 0,
				discountAmount: Number(item.discountAmount) || 0,
				taxRate: Number(item.taxPercentage ?? item.taxRate ?? 0) || 0,
				taxAmount: Number(item.taxAmount) || 0,
				totalAmount: Number(item.totalAmount) || 0,
			})),
		};
	};

	const handleItemChange = (itemId: string, field: keyof PurchaseItem, value: any) => {
		if (purchaseTransaction) {
			const updatedItems = purchaseTransaction.items.map(item => {
				if (item.id === itemId) {
					const updatedItem = { ...item, [field]: value };
					const discountPct =
						Number(
							updatedItem.discountPercentage ?? (updatedItem as { discountRate?: number }).discountRate,
						) || 0;
					const taxPct = Number(updatedItem.taxPercentage ?? updatedItem.taxRate) || 0;

					// Recalculate amounts
					const discountAmount = (updatedItem.unitPrice * updatedItem.quantity * discountPct) / 100;
					const taxAmount = ((updatedItem.unitPrice * updatedItem.quantity - discountAmount) * taxPct) / 100;
					const totalAmount = updatedItem.unitPrice * updatedItem.quantity - discountAmount + taxAmount;

					return {
						...updatedItem,
						discountAmount,
						taxAmount,
						totalAmount,
					};
				}
				return item;
			});

			// Recalculate totals
			const subtotal = updatedItems.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
			const totalDiscount = updatedItems.reduce((sum, item) => sum + item.discountAmount, 0);
			const totalTax = updatedItems.reduce((sum, item) => sum + item.taxAmount, 0);
			const totalAmount = subtotal - totalDiscount + totalTax + (purchaseTransaction.shippingAmount ?? 0);

			setPurchaseTransaction(prev => ({
				...prev!,
				items: updatedItems,
				subtotal,
				discountAmount: totalDiscount,
				taxAmount: totalTax,
				totalAmount,
			}));
		}
	};

	const handleSubmit = async () => {
		try {
			setSaving(true);
			if (purchaseTransaction) {
				await purchasesAPI.update(id!, buildPurchaseUpdatePayload(purchaseTransaction));
				toast({
					title: t("common.success"),
					description: t("purchases.messages.updated"),
				});
			}
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleDelete = async () => {
		if (await confirmAction(t("purchases.confirmations.delete"), { confirmLabel: t("common.delete") })) {
			try {
				await purchasesAPI.delete(id!);
				toast({
					title: t("common.success"),
					description: t("purchases.messages.deleted"),
				});
				navigate("/purchases");
			} catch (error) {
				const payload = getApiErrorPayload(error);
				const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
				toast({
					title: t("common.error"),
					description: errorMessage || t("common.errors.unexpected"),
					variant: "destructive",
				});
			}
		}
	};

	const changeStatus = async (status: "received" | "cancelled") => {
		const confirmed = await confirmAction(
			t(
				status === "received"
					? "purchases.confirmations.markReceived"
					: "purchases.confirmations.cancelPurchase",
			),
			{
				confirmLabel: t(
					status === "received" ? "purchases.actions.markReceived" : "purchases.actions.cancelPurchase",
				),
				destructive: status === "cancelled",
			},
		);
		if (!confirmed || !id) return;
		try {
			setStatusUpdating(true);
			await purchasesAPI.updateStatus(id, status);
			toast({
				title: t("common.success"),
				description: t(status === "received" ? "purchases.messages.received" : "purchases.messages.cancelled"),
			});
			const data = await purchasesAPI.getById(id);
			setPurchaseTransaction(data);
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setStatusUpdating(false);
		}
	};

	const generatePDF = async () => {
		if (!id) return;
		try {
			setPdfLoading(true);
			const blob = await purchasesAPI.downloadPdf(id);
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `purchase-${purchaseTransaction?.invoiceNumber ?? id}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
			toast({
				title: t("common.success"),
				description: t("purchases.messages.pdfGenerated"),
			});
		} catch (error) {
			const payload = getApiErrorPayload(error);
			toast({
				title: t("common.error"),
				description:
					t(payload.key, (payload.params ?? {}) as Record<string, string>) ||
					t("purchases.errors.pdfGenerationFailed"),
				variant: "destructive",
			});
		} finally {
			setPdfLoading(false);
		}
	};

	const getStatusBadge = (status: string) => {
		const statusConfig: Record<string, { variant: string; text: string }> = {
			pending: { variant: "warning", text: t("purchases.status.pending") },
			confirmed: { variant: "success", text: t("purchases.status.confirmed") },
			received: { variant: "success", text: t("purchases.status.received") },
			cancelled: { variant: "destructive", text: t("purchases.status.cancelled") },
			refunded: { variant: "default", text: t("purchases.statuses.refunded") },
		};
		const config = statusConfig[status] || { variant: "default", text: status };
		return <Badge variant={config.variant as any}>{config.text}</Badge>;
	};

	const getPaymentStatusBadge = (status: string) => {
		const statusConfig: Record<string, { variant: string; text: string }> = {
			pending: { variant: "warning", text: t("purchases.paymentStatus.pending") },
			partial: { variant: "default", text: t("purchases.paymentStatus.partial") },
			paid: { variant: "success", text: t("purchases.paymentStatus.paid") },
		};
		const config = statusConfig[status] || { variant: "default", text: status };
		return <Badge variant={config.variant as any}>{config.text}</Badge>;
	};

	const isConfirmed = purchaseTransaction?.status === "confirmed";
	const canChangeStatus = purchaseTransaction?.status === "pending" || purchaseTransaction?.status === "confirmed";

	if (loading) return <Loading />;

	if (!purchaseTransaction) {
		return (
			<div className="text-center py-8">
				<p className="text-muted-foreground">{t("purchases.errors.notFound")}</p>
			</div>
		);
	}

	return (
		<div className="container mx-auto max-w-full space-y-4 px-3 py-4 md:px-4 md:py-5">
			<div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("purchases.detail.title")}</PageHeading>
						<p className="text-muted-foreground">
							{t("purchases.detail.description")} #{purchaseTransaction.invoiceNumber}
						</p>
					</div>
				</div>
				<div className="flex flex-wrap items-center gap-2">
					<Button variant="outline" onClick={generatePDF} disabled={pdfLoading}>
						<Download className="h-4 w-4 mx-2" />
						{pdfLoading ? t("common.loading") : t("purchases.actions.downloadPDF")}
					</Button>
					{canChangeStatus && (
						// Same permissions as the backend status endpoint (either one).
						<PermissionGate permission={["purchases.update", "purchases.approve"]}>
							<Button onClick={() => changeStatus("received")} disabled={statusUpdating}>
								<PackageCheck className="h-4 w-4 mx-2" />
								{t("purchases.actions.markReceived")}
							</Button>
							<Button
								variant="outline"
								onClick={() => changeStatus("cancelled")}
								disabled={statusUpdating}
							>
								<XCircle className="h-4 w-4 mx-2" />
								{t("purchases.actions.cancelPurchase")}
							</Button>
						</PermissionGate>
					)}
					{!isConfirmed && (
						<PermissionGate permission="purchases.update">
							<Button variant="outline" onClick={() => navigate(`/purchases/${id}/edit`)}>
								<Edit className="h-4 w-4 mx-2" />
								{t("common.edit")}
							</Button>
						</PermissionGate>
					)}
					<PermissionGate permission="purchases.delete">
						<Button variant="destructive" onClick={handleDelete}>
							<Trash2 className="h-4 w-4 mx-2" />
							{t("common.delete")}
						</Button>
					</PermissionGate>
				</div>
			</div>

			<div className="space-y-4">
				{isConfirmed && (
					<p className="text-sm text-muted-foreground rounded-lg bg-muted p-3">
						{t(
							"purchases.detail.confirmedReadOnly",
							"Confirmed purchases cannot be edited. Add payments from the purchase if needed.",
						)}
					</p>
				)}
				<Card>
					<CardHeader>
						<CardTitle className="flex flex-wrap items-center justify-between gap-3">
							{t("purchases.detail.invoiceInfo")}
							<div className="flex flex-wrap items-center gap-x-6 gap-y-3">
								<div className="flex items-center gap-2">
									<Label>{t("purchases.detail.status")}</Label>
									{getStatusBadge(purchaseTransaction.status)}
								</div>
								<div className="flex items-center gap-2">
									<Label>{t("purchases.detail.paymentStatus")}</Label>
									{getPaymentStatusBadge(purchaseTransaction.paymentStatus)}
								</div>
							</div>
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
							<div className="space-y-2">
								<Label>{t("purchases.detail.invoiceNumber")}</Label>
								<Input
									aria-label={t("purchases.detail.invoiceNumber")}
									value={purchaseTransaction.invoiceNumber}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("purchases.detail.purchaseDate")}</Label>
								<Input
									aria-label={t("purchases.detail.purchaseDate")}
									type="date"
									value={
										purchaseTransaction.purchaseDate
											? String(purchaseTransaction.purchaseDate).slice(0, 10)
											: ""
									}
									onChange={e => handleInputChange("purchaseDate", e.target.value)}
									readOnly={isConfirmed}
								/>
							</div>
						</div>

						<div className="space-y-2">
							<Label>{t("purchases.detail.notes")}</Label>
							<Input
								aria-label={t("purchases.detail.notes")}
								type="text"
								value={purchaseTransaction.notes ?? ""}
								onChange={e => handleInputChange("notes", e.target.value)}
								readOnly={isConfirmed}
							/>
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("purchases.detail.items")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							{purchaseTransaction.items.map(item => (
								<div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
									<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
										{item.productImage ? (
											<img
												src={item.productImage}
												alt={item.productName}
												className="w-full h-full object-cover rounded-lg"
											/>
										) : (
											<Package className="h-8 w-8 text-primary" />
										)}
									</div>
									<div className="flex-1">
										<h4 className="font-medium">{item.productName}</h4>
										<div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-2">
											<div>
												<Label className="text-sm">{t("purchases.detail.quantity")}</Label>
												<Input
													aria-label={t("purchases.detail.quantity")}
													type="number"
													min="1"
													value={item.quantity}
													onChange={e =>
														handleItemChange(
															item.id,
															"quantity",
															parseInt(e.target.value) || 1,
														)
													}
													className="w-20"
													readOnly={isConfirmed}
												/>
											</div>
											<div>
												<Label className="text-sm">{t("purchases.detail.unitPrice")}</Label>
												<Input
													aria-label={t("purchases.detail.unitPrice")}
													type="number"
													min="0"
													step="0.01"
													value={item.unitPrice}
													onChange={e =>
														handleItemChange(
															item.id,
															"unitPrice",
															parseFloat(e.target.value) || 0,
														)
													}
													className="w-24"
													readOnly={isConfirmed}
												/>
											</div>
											<div>
												<Label className="text-sm">{t("purchases.detail.discount")}</Label>
												<Input
													aria-label={t("purchases.detail.discount")}
													type="number"
													min="0"
													max="100"
													step="0.01"
													value={
														item.discountPercentage ??
														(item as { discountRate?: number }).discountRate ??
														0
													}
													onChange={e =>
														handleItemChange(
															item.id,
															"discountPercentage",
															parseFloat(e.target.value) || 0,
														)
													}
													className="w-20"
													readOnly={isConfirmed}
												/>
											</div>
											<div>
												<Label className="text-sm">{t("purchases.add.taxPercent")} </Label>
												<Input
													aria-label={t("purchases.add.taxPercent")}
													type="number"
													min="0"
													max="100"
													step="0.01"
													value={
														item.taxPercentage ??
														(item as { taxRate?: number }).taxRate ??
														0
													}
													onChange={e =>
														handleItemChange(
															item.id,
															"taxPercentage",
															parseFloat(e.target.value) || 0,
														)
													}
													className="w-20"
													readOnly={isConfirmed}
												/>
											</div>
											<div>
												<Label className="text-sm">{t("purchases.detail.total")}</Label>
												<div className="text-lg font-semibold">
													{formatNumber(item.totalAmount)}
												</div>
											</div>
										</div>
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle>{t("purchases.detail.summary")}</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="space-y-4">
							<div className="flex justify-between">
								<span>{t("purchases.detail.subtotal")}</span>
								<span className="font-semibold">
									<Price value={purchaseTransaction.subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("purchases.detail.discount")}</span>
								<span className="font-semibold text-destructive">
									-<Price value={purchaseTransaction.discountAmount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("purchases.detail.tax")}</span>
								<span className="font-semibold">
									<Price value={purchaseTransaction.taxAmount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("purchases.detail.shipping")}</span>
								<span className="font-semibold">
									<Price value={purchaseTransaction.shippingAmount ?? 0} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("purchases.detail.total")}</span>
									<span>
										<Price value={purchaseTransaction.totalAmount} currency={currency} />
									</span>
								</div>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle>{t("purchases.detail.supplierInfo")}</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
							<div className="space-y-2">
								<Label>{t("purchases.detail.supplierName")}</Label>
								<Input
									aria-label={t("purchases.detail.supplierName")}
									value={purchaseTransaction.supplier?.name}
									readOnly
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("purchases.detail.supplierEmail")}</Label>
								<Input
									aria-label={t("purchases.detail.supplierEmail")}
									value={purchaseTransaction.supplier?.email}
									readOnly
								/>
							</div>
						</div>

						<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
							<div className="space-y-2">
								<Label>{t("purchases.detail.supplierPhone")}</Label>
								<Input
									aria-label={t("purchases.detail.supplierPhone")}
									value={purchaseTransaction.supplier?.phone}
									readOnly
									className="rtl:text-right"
									dir="ltr"
								/>
							</div>
							<div className="space-y-2">
								<Label>{t("purchases.detail.supplierAddress")}</Label>
								<Input
									aria-label={t("purchases.detail.supplierAddress")}
									value={purchaseTransaction.supplier?.address}
									readOnly
								/>
							</div>
						</div>
					</CardContent>
				</Card>
				<Card>
					<CardHeader>
						<CardTitle>{t("purchases.detail.companyInfo")}</CardTitle>
					</CardHeader>
					<CardContent className="space-y-6">
						{companyProfile && (
							<>
								<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
									<div className="space-y-2">
										<Label>{t("purchases.detail.companyName")}</Label>
										<Input
											aria-label={t("purchases.detail.companyName")}
											value={companyProfile.name}
											readOnly
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("purchases.detail.companyEmail")}</Label>
										<Input
											aria-label={t("purchases.detail.companyEmail")}
											value={companyProfile.email}
											readOnly
										/>
									</div>
								</div>

								<div className="grid grid-cols-1 gap-3 md:grid-cols-2">
									<div className="space-y-2">
										<Label>{t("purchases.detail.companyPhone")}</Label>
										<Input
											aria-label={t("purchases.detail.companyPhone")}
											value={companyProfile.phone}
											readOnly
											className="rtl:text-right"
											dir="ltr"
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("purchases.detail.companyWebsite")}</Label>
										<Input
											aria-label={t("purchases.detail.companyWebsite")}
											value={companyProfile.website}
											readOnly
										/>
									</div>
								</div>

								<div className="space-y-2">
									<Label>{t("purchases.detail.companyAddress")}</Label>
									<Input
										aria-label={t("purchases.detail.companyAddress")}
										value={companyProfile.address}
										readOnly
									/>
								</div>
							</>
						)}
					</CardContent>
				</Card>
			</div>

			{!isConfirmed && (
				<div className="flex justify-end">
					<Button onClick={handleSubmit} disabled={saving}>
						{saving ? (
							<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
						) : (
							<Eye className="h-4 w-4 mx-2" />
						)}
						{t("common.save")}
					</Button>
				</div>
			)}
		</div>
	);
};

export default PurchaseDetailPage;
